import { FILE_TYPE_DISPLAY, type FileTypeId } from '@steerfi/shared';
import { IconArrowLeft } from '@tabler/icons-react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { FileFormatApiRow } from '@/api/file-formats';
import { DeleteFileFormatDialog } from '@/components/file-formats/delete-file-format-dialog';
import { EditFileFormatModal } from '@/components/file-formats/edit-file-format-modal';
import {
  formatScopeSummary,
  inferScopeFromRow,
  JSONB_FIELDS,
} from '@/components/file-formats/file-format-form.utils';
import { RestoreFileFormatDialog } from '@/components/file-formats/restore-file-format-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/contexts/document-title-context';
import { useFileFormat } from '@/hooks/use-file-formats';
import { formatDate } from '@/lib/utils';

const CONFIG_FIELDS = JSONB_FIELDS.map(({ key, label }) => ({ key, label }));

interface DetailFieldProps {
  label: string;
  children: React.ReactNode;
}

const DetailField: React.FC<DetailFieldProps> = ({ label, children }) => (
  <div>
    <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
      {label}
    </p>
    <div className="text-sm">{children}</div>
  </div>
);

const StatusBadge: React.FC<{ deletedAt: string | null }> = ({ deletedAt }) =>
  deletedAt ? (
    <Badge
      variant="outline"
      className="text-xs font-medium bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800"
    >
      Deleted
    </Badge>
  ) : (
    <Badge
      variant="outline"
      className="text-xs font-medium bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
    >
      Active
    </Badge>
  );

interface ConfigSectionProps {
  row: FileFormatApiRow;
}

// Renders each populated JSONB column as a labelled, monospaced JSON block.
// Empty/null columns are collapsed into a single "No advanced configuration
// set." line so the section never renders an inconsistent mix of "—" rows.
const ConfigSection: React.FC<ConfigSectionProps> = ({ row }) => {
  const populated = useMemo(() => CONFIG_FIELDS.filter(({ key }) => row[key] != null), [row]);

  if (populated.length === 0) {
    return <p className="text-sm text-muted-foreground">No advanced configuration set.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {populated.map(({ key, label }) => (
        <div key={String(key)}>
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
            {label}
          </p>
          <pre className="rounded-md border bg-muted/30 p-3 text-xs leading-relaxed font-mono overflow-x-auto whitespace-pre-wrap break-words">
            {JSON.stringify(row[key], null, 2)}
          </pre>
        </div>
      ))}
    </div>
  );
};

export const FileFormatDetail: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // URL ids are strings; the backend's primary key is a serial integer.
  // Coerce here so the hook can fast-fail on `NaN` instead of issuing a
  // bogus request.
  const id = idParam ? Number(idParam) : undefined;

  const { row, isLoading, error, notFound, refetch } = useFileFormat(id);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);

  useDocumentTitle(row?.format_name);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Loading file format…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">File format not found.</p>
        <Link to="/file-formats" className="text-sm underline underline-offset-4">
          Back to File Formats
        </Link>
      </div>
    );
  }

  if (error || !row) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
          {error ?? 'Failed to load file format.'}
        </div>
        <Link to="/file-formats" className="text-sm underline underline-offset-4">
          Back to File Formats
        </Link>
      </div>
    );
  }

  const typeLabel = FILE_TYPE_DISPLAY[row.file_type_id as FileTypeId] ?? String(row.file_type_id);
  const scopeKind = inferScopeFromRow(row);
  const scopeLabel = formatScopeSummary(scopeKind, row.party);
  const isDeleted = row.deleted_at != null;

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground -ml-2 mb-2"
          onClick={() => navigate('/file-formats')}
        >
          <IconArrowLeft size={14} />
          File Formats
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">{row.format_name}</h1>
        <p className="text-sm text-muted-foreground mt-0.5 font-mono">{row.code}</p>
      </div>

      <div className="px-4 lg:px-6">
        <div className="rounded-md border">
          <div className="flex items-start justify-between p-5 border-b">
            <div>
              <p className="font-medium text-sm">File format details</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Definition for how the platform parses or generates this payment file.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isDeleted ? (
                <Button variant="outline" size="sm" onClick={() => setRestoreOpen(true)}>
                  Restore
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive border-destructive/40 hover:bg-destructive/10"
                    onClick={() => setDeleteOpen(true)}
                  >
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 p-5">
            <DetailField label="Code">
              <span className="font-mono">{row.code}</span>
            </DetailField>
            <DetailField label="Name">{row.format_name}</DetailField>
            <DetailField label="Type">{typeLabel}</DetailField>
            <DetailField label="Version">v{row.version_number}</DetailField>
            <DetailField label="Scope">{scopeLabel}</DetailField>
            <DetailField label="Status">
              <StatusBadge deletedAt={row.deleted_at} />
            </DetailField>
            <DetailField label="Created">{formatDate(row.created_at)}</DetailField>
            <DetailField label="Updated">{formatDate(row.updated_at)}</DetailField>
            <div className="col-span-2 md:col-span-4">
              <DetailField label="Description">
                {row.description ? (
                  <p className="whitespace-pre-wrap">{row.description}</p>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </DetailField>
            </div>
            <div className="col-span-2 md:col-span-4">
              <DetailField label="S3 bucket name">
                {row.s3_bucket_name ?? <span className="text-muted-foreground">—</span>}
              </DetailField>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <div className="rounded-md border">
          <div className="p-5 border-b">
            <p className="font-medium text-sm">Configuration (advanced)</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Read-only view of the structural definition, mapping, and validation JSON.
            </p>
          </div>
          <div className="p-5">
            <ConfigSection row={row} />
          </div>
        </div>
      </div>

      <EditFileFormatModal
        open={editOpen}
        row={row}
        onOpenChange={setEditOpen}
        onSuccess={() => {
          setEditOpen(false);
          refetch();
        }}
      />
      <DeleteFileFormatDialog
        open={deleteOpen}
        row={row}
        onOpenChange={setDeleteOpen}
        onSuccess={() => navigate('/file-formats')}
      />
      <RestoreFileFormatDialog
        open={restoreOpen}
        row={row}
        onOpenChange={setRestoreOpen}
        onSuccess={() => {
          setRestoreOpen(false);
          refetch();
        }}
      />
    </div>
  );
};
